import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { buildRecommender } from '../lib/recommenderShape';
import type { Profile, Recommender, UserPick } from '../types';

interface RawProfileRow {
  user_id: string;
  slug: string;
  display_name: string;
  created_at: string;
}

interface RawPickRow {
  name: string;
  place_id: string;
  cuisine: string | null;
  location: string | null;
  position: number;
}

function isProfileRow(value: unknown): value is RawProfileRow {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.user_id === 'string' &&
    typeof v.slug === 'string' &&
    typeof v.display_name === 'string' &&
    typeof v.created_at === 'string'
  );
}

function isPickRow(value: unknown): value is RawPickRow {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.name === 'string' &&
    typeof v.place_id === 'string' &&
    typeof v.position === 'number'
  );
}

function rawToProfile(row: RawProfileRow): Profile {
  return {
    userId: row.user_id,
    slug: row.slug,
    displayName: row.display_name,
    createdAt: row.created_at,
  };
}

function rawToPick(row: RawPickRow): UserPick {
  return {
    name: row.name,
    placeId: row.place_id,
    cuisine: row.cuisine ?? '',
    location: row.location ?? '',
    position: row.position,
  };
}

interface UseRecommenderBySlugResult {
  recommender: Recommender | null;
  isLoaded: boolean;
  error: string | null;
}

export function useRecommenderBySlug(slug: string | undefined): UseRecommenderBySlugResult {
  const [recommender, setRecommender] = useState<Recommender | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) {
      setRecommender(null);
      setIsLoaded(true);
      return;
    }

    let mounted = true;
    setIsLoaded(false);

    (async () => {
      const profileRes = await supabase
        .from('profiles')
        .select('user_id, slug, display_name, created_at')
        .eq('slug', slug)
        .maybeSingle();
      if (!mounted) return;

      if (profileRes.error) {
        setError(profileRes.error.message);
        setRecommender(null);
        setIsLoaded(true);
        return;
      }
      if (!profileRes.data || !isProfileRow(profileRes.data)) {
        setRecommender(null);
        setIsLoaded(true);
        setError(null);
        return;
      }
      const profile = rawToProfile(profileRes.data);

      const picksRes = await supabase
        .from('user_picks')
        .select('name, place_id, cuisine, location, position')
        .eq('user_id', profile.userId)
        .order('position', { ascending: true });
      if (!mounted) return;

      if (picksRes.error) {
        setError(picksRes.error.message);
        setRecommender(null);
        setIsLoaded(true);
        return;
      }

      const picks = Array.isArray(picksRes.data)
        ? picksRes.data.filter(isPickRow).map(rawToPick)
        : [];

      setRecommender(buildRecommender(profile, picks));
      setIsLoaded(true);
      setError(null);
    })();

    return () => {
      mounted = false;
    };
  }, [slug]);

  return { recommender, isLoaded, error };
}
